import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Text,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  type PinpointSheetType,
  getPinpointSceneAndFieldBySheetType,
} from '@/features/comment/utils/pinpoint';
import type {
  ActivityFlowEdgeModel,
  ActivityModel,
  IndicatorModel,
} from '@/features/project/projects/_models/activity';
import { useId } from '@/hooks';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { IndicatorSection } from './IndicatorSection';

type RouteIndicator = {
  indicator: z.infer<typeof IndicatorModel>;
  edge: z.infer<typeof ActivityFlowEdgeModel> | undefined;
};

type Props = {
  rootActivity: z.infer<typeof ActivityModel>;
  routeIndicators: RouteIndicator[];
  routeIndicatorId: string;
  pinpointSheetType: PinpointSheetType;
};

export const SelectedActivityFlowRouteTable: FC<Props> = ({
  rootActivity,
  routeIndicators,
  routeIndicatorId,
  pinpointSheetType,
}) => {
  const { t } = useTranslation();
  const { id } = useId();

  const path = {
    project: id,
    routeIndicatorId,
    activityId: rootActivity.id,
  };

  return (
    <Flex vertical gap={16}>
      <ContentWrapper title={t('activity')} paddingTop={0}>
        <ContentItem borderBottom="0" paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="activity"
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType,
                  rs: {
                    path,
                    field: 'rs.activity',
                  },
                  ss: {
                    path,
                    field: 'ss.activity',
                  },
                  ksStandard: {
                    path,
                    field: 'ks.standard.activity',
                  },
                  ksLocalGovernment: {
                    path,
                    field: 'ks.local-government.activity',
                  },
                })}
              >
                <Text weight="regular" text={rootActivity.activityContent} />
              </PinpointMarker>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      {routeIndicators.map((routeIndicator, index, arr) => (
        <IndicatorSection
          key={`${routeIndicatorId}_${
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            index
          }`}
          routeIndicatorId={routeIndicatorId}
          currentRouteIndicator={routeIndicator}
          nextRouteIndicator={arr.at(index + 1)}
          pinpointSheetType={pinpointSheetType}
        />
      ))}
    </Flex>
  );
};
